import React from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";

import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import Onboarding from "@/pages/Onboarding";
import HomePage from "@/pages/Home";
import ListBuilder from "@/pages/ListBuilder";
import Results from "@/pages/Results";
import TripMode from "@/pages/TripMode";
import Discover from "@/pages/Discover";
import Trips from "@/pages/Trips";
import Pantry from "@/pages/Pantry";
import Alerts from "@/pages/Alerts";
import Receipts from "@/pages/Receipts";
import Profile from "@/pages/Profile";
import Admin from "@/pages/Admin";
import Insights from "@/pages/Insights";
import SharedList from "@/pages/SharedList";

const Splash = () => (
  <div className="min-h-screen grid place-items-center bg-pp-cream">
    <div className="pp-pop flex flex-col items-center gap-3">
      <img src="/pantry-pilot-logo.png" alt="" className="h-14 w-14 rounded-2xl animate-pulse" />
      <p className="text-pp-muted text-sm font-medium">Loading your best plan…</p>
    </div>
  </div>
);

function Protected({ children, admin }) {
  const { user } = useAuth();
  if (user === null) return <Splash />;
  if (!user) return <Navigate to="/login" replace />;
  if (user && user.onboarded === false) return <Navigate to="/onboarding" replace />;
  if (admin && user.role !== "admin") return <Navigate to="/" replace />;
  return <Layout>{children}</Layout>;
}

function Shell() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/reset-password" element={<Login />} />
      <Route path="/shared/:token" element={<SharedList />} />
      <Route path="/onboarding" element={<OnboardingGate />} />
      <Route path="/" element={<Protected><HomePage /></Protected>} />
      <Route path="/list" element={<Protected><ListBuilder /></Protected>} />
      <Route path="/list/:listId" element={<Protected><ListBuilder /></Protected>} />
      <Route path="/list/:listId/results" element={<Protected><Results /></Protected>} />
      <Route path="/trip/:tripId" element={<Protected><TripMode /></Protected>} />
      <Route path="/discover" element={<Protected><Discover /></Protected>} />
      <Route path="/trips" element={<Protected><Trips /></Protected>} />
      <Route path="/pantry" element={<Protected><Pantry /></Protected>} />
      <Route path="/alerts" element={<Protected><Alerts /></Protected>} />
      <Route path="/receipts" element={<Protected><Receipts /></Protected>} />
      <Route path="/profile" element={<Protected><Profile /></Protected>} />
      <Route path="/savings" element={<Protected><Insights /></Protected>} />
      <Route path="/admin" element={<Protected admin><Admin /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function OnboardingGate() {
  const { user } = useAuth();
  if (user === null) return <Splash />;
  if (!user) return <Navigate to="/login" replace />;
  return <Onboarding />;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Shell />
        <Toaster position="top-center" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}
